/* eslint camelcase: "off" */
import axios from 'axios';
import { COINGECKO_ENDPOINT } from '~/config.js';
import { dedupeInFlight } from '~/api/dedupe.js';

/**
 * @see https://www.coingecko.com/en/api/documentation
 */
const http = axios.create({
    baseURL: COINGECKO_ENDPOINT,
    // The proxy either answers within a couple of seconds or never does:
    // a hung request is given up on so the caller can retry it.
    timeout: 8000,
});

/**
 * Get historical market data include price, market cap, and 24h volume (granularity auto).
 *
 * Identical requests made at the same moment share one response: the chart is
 * the heaviest request on the page (a month of prices is a quarter of a
 * megabyte) and the mobile index asks for the same one twice — from the price
 * block and from the store. Callers must not mutate what they get back.
 *
 * @param  {Number} options.days
 * @param  {String} options.currency
 * @param  {String} options.interval
 * @return {Promise<Object>}
 */
export const getMarketDataHistory = dedupeInFlight(async ({ days, currency, interval, precision }) => {
    const params = {
        days, interval,
        vs_currency: (currency || 'USD').toUpperCase(),
        precision: precision || 4,
    };

    const { data } = await http.get('/chart', { params });

    // { prices, market_caps, total_volumes }
    return data;

// The key ignores the case of the currency, since the request uppercases it anyway:
}, ({ days, currency, interval, precision } = {}) => [
    days, String(currency || 'USD').toUpperCase(), interval, precision,
].join(':'));

/**
 * @param  {String} currency
 * @return {Promise<Array[Number, Number]>}
 */
export const getExchangeRateAndTrend = async function getBriefMarketInfo(currency) {
    const { prices } = await getMarketDataHistory({
        currency,
        days: 2,
        interval: 'daily',
    });

    if (!prices?.length) {
        throw new Error('No prices in market data response');
    }

    // Read, don't splice: the response may be shared with another caller.
    const oldest = prices[0][1];
    const newest = prices[prices.length - 1][1];
    const trend = newest >= oldest ? 1 : -1;

    return [newest, trend];
};
